import AccessAlarmIcon from '@mui/icons-material/AccessAlarm'
import { Alert, Box, Button, Snackbar } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { AuthState, destroyAuthSession, getSessionExpiry, useAuth } from './context'
import { useExtendUserSession } from './extendSession'
import { AUTH_SESSION_AUTO_REFRESH_INTERVAL_MINUTES, AUTH_SESSION_SHOW_EXPIRING_NOTIFICATION } from './helpers'

const API_URL = import.meta.env.VITE_API_URL

type Props = {
  onUpdateSession: (authState: AuthState | null) => void
}

export const SessionExpiryNotification = ({ onUpdateSession }: Props) => {
  // Track remaining time only when we need to show the UI
  const [timeRemaining, setTimeRemaining] = useState<{ minutes: number; seconds: number } | null>(null)

  const [extendSession, { loading }] = useExtendUserSession()
  const authState = useAuth()
  const navigate = useNavigate()

  // Failed extend (falsy) = session gone: navigate away rather than nulling the live
  // context under a mounted protected route, which would throw (RDM-FRONTEND-3KE).
  const applySessionUpdate = (newState: AuthState | null) => {
    if (!newState) {
      destroyAuthSession()
      navigate('/session-expired')
      return
    }
    onUpdateSession(newState)
  }

  // 1. On page load: check if we need to auto-extend the session
  useEffect(() => {
    const expiryDate = getSessionExpiry()
    if (!expiryDate) return

    if (expiryDate.diff(dayjs(), 'minute') < AUTH_SESSION_AUTO_REFRESH_INTERVAL_MINUTES) {
      extendSession().then(applySessionUpdate).catch(console.error)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run exactly once on mount

  // 2. Timer: continuously check for session expiry
  useEffect(() => {
    const interval = setInterval(() => {
      // Not logged in — there's no session to expire (avoids bouncing public pages to
      // /session-expired).
      if (!authState) return

      const now = dayjs()
      const expiryDate = getSessionExpiry()

      // Session has lapsed — log out. Either we're past the expiry instant, or the stored
      // session is already gone: getAuthStateFromSession() deletes it (so getSessionExpiry()
      // returns null) the moment it expires. The old `if (!expiryDate) return` guard sat
      // BEFORE this check, so a null expiry skipped logout entirely — leaving the countdown
      // frozen and the user stranded on a dead session (common in throttled background tabs,
      // which miss the sub-second window where diffSeconds first hits 0).
      if (!expiryDate || expiryDate.diff(now, 'second') <= 0) {
        clearInterval(interval)
        setTimeRemaining(null)

        // Close the server session (fire-and-forget)
        if (authState.token) {
          fetch(`${API_URL}/auth/logout`, {
            headers: { UQRDM_TOKEN: authState.token },
          }).catch(console.error)
        }

        destroyAuthSession()
        navigate('/session-expired')
        return
      }

      const diffMinutes = expiryDate.diff(now, 'minute')
      const diffSeconds = expiryDate.diff(now, 'second')
      if (diffMinutes < AUTH_SESSION_SHOW_EXPIRING_NOTIFICATION) {
        setTimeRemaining({ minutes: diffMinutes, seconds: diffSeconds })
      } else if (timeRemaining !== null) {
        setTimeRemaining(null)
      }
    }, 1000)

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, authState?.token])

  const onExtend = async () => {
    try {
      const newSession = await extendSession()
      applySessionUpdate(newSession)
      setTimeRemaining(null)
    } catch (error) {
      console.error('Failed to extend session:', error)
    }
  }

  if (!authState || !timeRemaining) return null

  const isMinutes = timeRemaining.minutes > 1
  const displayTime = isMinutes ? `${timeRemaining.minutes} minutes` : `${timeRemaining.seconds} seconds`

  return (
    <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={true}>
      <Alert
        role="alert"
        severity="error"
        icon={<AccessAlarmIcon className="shakeme" />}
        sx={{ width: '100%' }}
        action={
          <Button variant="outlined" color="error" size="small" onClick={onExtend} disabled={loading}>
            {loading ? 'Extending...' : 'Stay logged in'}
          </Button>
        }
      >
        <Box sx={{ mt: 0.2, p: 0 }}>
          Your session will expire in <b>{displayTime}</b> due to inactivity.
        </Box>
      </Alert>
    </Snackbar>
  )
}
